import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from './Dashboard';
import { CheckCircle, XCircle, Hourglass, Rocket } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8;

const PaymentSuccess = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('pending');
  const [planId, setPlanId] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const pollTimerRef = useRef(null);

  const pollStatus = useCallback(async (attempt) => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    if (attempt >= MAX_POLL_ATTEMPTS) {
      setStatus('timeout');
      return;
    }
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${sessionId}`,
        { withCredentials: true }
      );
      const data = response.data;
      setPlanId(data.plan_id);
      if (data.payment_status === 'paid') {
        setStatus('paid');
        return;
      }
      if (data.status === 'expired') {
        setStatus('expired');
        return;
      }
      setAttempts(attempt + 1);
      pollTimerRef.current = setTimeout(() => pollStatus(attempt + 1), POLL_INTERVAL_MS);
    } catch {
      setStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    pollStatus(0);
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [pollStatus]);

  const renderContent = () => {
    if (status === 'pending') {
      return (
        <>
          <Hourglass size={64} weight="bold" className="text-[#F59E0B] animate-pulse mx-auto mb-6" />
          <h1 className="text-3xl font-[Outfit] font-bold mb-3">{t('paymentSuccess.processing_title')}</h1>
          <p className="text-white/60 mb-6">
            {t('paymentSuccess.processing_sub', { current: attempts, max: MAX_POLL_ATTEMPTS })}
          </p>
          <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto"></div>
        </>
      );
    }
    if (status === 'paid') {
      return (
        <>
          <CheckCircle size={64} weight="fill" className="text-[#10B981] mx-auto mb-6" />
          <h1 className="text-4xl font-[Outfit] font-bold mb-3">{t('paymentSuccess.paid_title')}</h1>
          <p className="text-white/60 mb-2">
            {t('paymentSuccess.paid_welcome')} <span className="font-bold text-white capitalize">{planId}</span> {t('paymentSuccess.paid_plan_suffix')} 🎉
          </p>
          <p className="text-white/60 mb-8">{t('paymentSuccess.paid_sub')}</p>
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="go-to-dashboard"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg font-medium transition-all"
          >
            <Rocket size={20} weight="bold" />
            {t('paymentSuccess.go_dashboard')}
          </button>
        </>
      );
    }
    if (status === 'expired') {
      return (
        <>
          <XCircle size={64} weight="fill" className="text-[#EF4444] mx-auto mb-6" />
          <h1 className="text-3xl font-[Outfit] font-bold mb-3">{t('paymentSuccess.expired_title')}</h1>
          <p className="text-white/60 mb-6">{t('paymentSuccess.expired_sub')}</p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-6 py-3 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg font-medium"
          >
            {t('paymentSuccess.back_to_plans')}
          </button>
        </>
      );
    }
    // error or timeout
    return (
      <>
        <Hourglass size={64} weight="bold" className="text-white/40 mx-auto mb-6" />
        <h1 className="text-3xl font-[Outfit] font-bold mb-3">{t('paymentSuccess.slow_title')}</h1>
        <p className="text-white/60 mb-6">
          {t('paymentSuccess.slow_sub')}
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-6 py-3 bg-[#2563EB] hover:bg-[#3B82F6] rounded-lg font-medium"
        >
          {t('paymentSuccess.go_dashboard')}
        </button>
      </>
    );
  };

  return (
    <DashboardLayout user={user} activePage="">
      <div className="p-6" data-testid="payment-success-page">
        <div className="max-w-md mx-auto mt-20 text-center p-8 bg-[#121212] border border-white/10 rounded-2xl">
          {renderContent()}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PaymentSuccess;
